import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { AuthContext, API } from '../App';
import { Loader2, ArrowLeft, Sparkles, Copy, Check, Linkedin } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { PackageBanner } from '../components/PackageBanner';
import axios from 'axios';
import { toast } from 'sonner';

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(text); setCopied(true); toast.success('Copied'); setTimeout(() => setCopied(false), 2000); }}>
      {copied ? <><Check className="w-3.5 h-3.5 mr-1" /> Copied</> : <><Copy className="w-3.5 h-3.5 mr-1" /> Copy</>}
    </Button>
  );
}

export default function LinkedInTipsPage() {
  const { user } = useContext(AuthContext);
  const [currentRole, setCurrentRole] = useState('');
  const [targetRole, setTargetRole] = useState('');
  const [generating, setGenerating] = useState(false);
  const [tips, setTips] = useState(null);

  const handleGenerate = async () => {
    if (!currentRole.trim()) { toast.error('Enter your current role'); return; }
    if (!targetRole.trim()) { toast.error('Enter your target role'); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/linkedin/generate-tips`, {
        current_role: currentRole.trim(),
        target_role: targetRole.trim()
      });
      setTips(res.data.tips);
      toast.success('LinkedIn tips generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate tips');
    } finally { setGenerating(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <div className="container mx-auto px-4 py-8 max-w-4xl flex-1">
        <PackageBanner />
        <div className="flex items-center gap-2 mb-6">
          <Link to="/dashboard" className="text-gray-400 hover:text-gray-600"><ArrowLeft className="w-5 h-5" /></Link>
          <h1 className="text-3xl font-bold" data-testid="linkedin-tips-title">LinkedIn Profile Optimization</h1>
        </div>

        {!tips ? (
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Linkedin className="w-5 h-5 text-blue-600" />
              <h2 className="text-xl font-bold">Generate LinkedIn Tips</h2>
            </div>
            <p className="text-sm text-gray-600 mb-6">Get AI-powered tips to optimize your LinkedIn profile for UAE recruiters.</p>
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Role *</label>
                <input type="text" value={currentRole} onChange={e => setCurrentRole(e.target.value)} placeholder="e.g. Sales Executive"
                  className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="current-role-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Role *</label>
                <input type="text" value={targetRole} onChange={e => setTargetRole(e.target.value)} placeholder="e.g. Sales Manager"
                  className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="target-role-input" />
              </div>
            </div>
            <Button onClick={handleGenerate} disabled={generating} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="generate-linkedin-btn">
              {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate LinkedIn Tips</>}
            </Button>
          </Card>
        ) : (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold">Your LinkedIn Optimization Plan</h2>
              <Button variant="outline" size="sm" onClick={() => setTips(null)}>New Tips</Button>
            </div>

            {/* Headlines */}
            <Card className="p-5">
              <h3 className="font-semibold mb-3 text-blue-700">Headline Suggestions</h3>
              <div className="space-y-2">
                {(tips.headline_suggestions || []).map((h, i) => (
                  <div key={i} className="flex items-center justify-between bg-blue-50 rounded-lg p-3">
                    <p className="text-sm font-medium">{h}</p>
                    <CopyBtn text={h} />
                  </div>
                ))}
              </div>
            </Card>

            {/* About Section */}
            <Card className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-blue-700">Optimized 'About' Section</h3>
                <CopyBtn text={tips.about_section || ''} />
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm leading-relaxed whitespace-pre-line">{tips.about_section}</p>
              </div>
            </Card>

            {/* Profile Tips */}
            <Card className="p-5">
              <h3 className="font-semibold mb-3 text-blue-700">Profile Optimization Tips</h3>
              <div className="space-y-3">
                {(tips.profile_tips || []).map((t, i) => (
                  <div key={i} className="border rounded-lg p-3">
                    <p className="text-sm font-medium text-gray-900 mb-1">{t.section}</p>
                    <p className="text-sm text-gray-600">{t.tip}</p>
                  </div>
                ))}
              </div>
            </Card>

            {/* Keywords */}
            {tips.keyword_suggestions && (
              <Card className="p-5">
                <h3 className="font-semibold mb-3 text-blue-700">Keywords to Add</h3>
                <div className="flex flex-wrap gap-2">
                  {tips.keyword_suggestions.map((k, i) => (
                    <span key={i} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm cursor-pointer hover:bg-blue-200"
                      onClick={() => { navigator.clipboard.writeText(k); toast.success(`Copied: ${k}`); }}>{k}</span>
                  ))}
                </div>
              </Card>
            )}

            {/* Connection Strategy */}
            {tips.connection_strategy && (
              <Card className="p-5">
                <h3 className="font-semibold mb-3 text-blue-700">Connection Strategy</h3>
                <p className="text-sm text-gray-700 leading-relaxed">{tips.connection_strategy}</p>
              </Card>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
